import { Buffer } from 'buffer'
import { initializeApp } from 'firebase/app'
import { isError, isNumber } from 'lodash'
import Resizer from 'react-image-file-resizer'

import { DE } from '../constants/languages'
import { mutation } from '../graphql'
import { GRAPHQL_ERROR_FLAG } from './graphql-grouping'
import { BUILD_ENV, STAGE, STAGES } from './stage'

const firebaseConfig = {
  apiKey: 'AIzaSyCZ79Gysnnmzylz6SH06iyCvJbEHm1eMXA',
  authDomain: 'aivy-dashboard.firebaseapp.com',
  projectId: 'aivy-dashboard',
  storageBucket: 'aivy-dashboard.appspot.com',
  messagingSenderId: '435564825954',
  appId: '1:435564825954:web:62ee88a09dc1b413e0f03b',
  measurementId: 'G-CC8BYBGHMK'
}

export const DIAGNOSTIC_VERSION = 2

export const S3_AIVY_LOGO =
  'https://aivy-assets.s3.eu-central-1.amazonaws.com/logo/aivylogo.png'

// Re-Export, damit die bestehenden `import { BUILD_ENV } from '../helpers'`
// unveraendert laufen. STAGE/STAGES kommen direkt aus './stage' – neue
// stage-abhaengige Logik nutzt STAGE, nicht BUILD_ENV.
export { BUILD_ENV }
export const NODE_ENV = process.env.NODE_ENV

export const HIGH_FIT_RANGE = (score) => {
  return isNumber(score) && score >= 71 && score <= 100
}

export const MIDDLE_FIT_RANGE = (score) => {
  return isNumber(score) && score >= 41 && score < 71
}

export const LOW_FIT_RANGE = (score) => {
  return isNumber(score) && score < 41
}

// Maps a fit/match score (0-100) to the red/yellow/green palette used across
// the team analysis (linear list view and radar dimension overlay).
export const getBackgroundColorPassung = (value, alpha) => {
  const match = Math.round(value)

  if (HIGH_FIT_RANGE(match)) {
    return alpha ? 'rgba(41, 204, 199, 0.5)' : '#29CCC7'
  } else if (MIDDLE_FIT_RANGE(match)) {
    return alpha ? 'rgba(255, 154, 59, 0.5)' : '#FF9A3B'
  } else {
    return alpha ? 'rgba(255, 138, 176, 0.5)' : '#ff8ab0'
  }
}

export const LOCALSTORAGE_ITEMS = {
  CREATE_CAREER_CONFIGURATION_DISABLE_INTRO:
    'CREATE_CAREER_CONFIGURATION_INTRO',
  HIDE_TEAM_ANALYSE_INTRO: 'HIDE_TEAM_ANALYSE_INTRO',
  // JSON-Map { dedupeKey: sentAt }, wird bei jedem Schreiben auf < 24 h
  // gekuerzt – ein Key pro Payment und Fall waechst sonst unbegrenzt.
  PAYMENT_ALERT_COOLDOWN: 'PAYMENT_ALERT_COOLDOWN'
}

export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/

export const RESTIFY_DEV_URL =
  'https://ip28t9adxa.execute-api.eu-central-1.amazonaws.com'
export const RESTIFY_STAGING_URL =
  'https://6hsjl256ka.execute-api.eu-central-1.amazonaws.com'
export const RESTIFY_PROD_URL = 'https://restify.aivy.app'

const AIVY_OS_URL = {
  [STAGES.DEVELOP]: 'https://develop.os.aivy.app',
  [STAGES.STAGING]: 'https://staging.os.aivy.app',
  [STAGES.PRODUCTION]: 'https://os.aivy.app'
}

export const HUBSPOT_IFRAME_ID = 'hubspot-messages-iframe-container'

// Buchungsseite auf der Website. Bewusst NICHT der HubSpot-Kalenderlink:
// persönliche Kalender (z. B. /mahschid, /kammer) laufen tot, sobald jemand
// das Team verlässt – die Website-Seite bleibt stabil.
export const AIVY_MEETING_URL = 'https://www.aivy.app/gespraech-vereinbaren'

// Die partner_id existiert nur im Backend, gegen das dieses Dashboard laeuft.
// Ein Production-OS-Link mit einer Develop-partner_id ist ein toter Klick.
// Deshalb STAGE (Backend) und nicht BUILD_ENV (Branchname), siehe ./stage.
export const getAivyOsCustomerUrl = ({ partner_id }) =>
  `${AIVY_OS_URL[STAGE]}/customer/${partner_id}`

export const getCurrencyString = (price, locale = DE, currency = 'EUR') => {
  if (price === undefined || price === null) return '-'
  if (typeof price === 'string' || price instanceof String) {
    price = parseInt(price)
  }

  const res = (price / 100).toLocaleString(locale, {
    style: 'currency',
    currency
  })
  return res
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export function hexToRGB(hex, opacity) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  const r = parseInt(result[1], 16)
  const g = parseInt(result[2], 16)
  const b = parseInt(result[3], 16)

  return `rgba(${r}, ${g}, ${b}, ${opacity || 1})`
}

// https://stackoverflow.com/a/2117523
export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8

    return v.toString(16)
  })
}

export function uuidSmall() {
  return 'xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8

    return v.toString(16)
  })
}

/**
 * Checks if a parameter is set, i.e. not null or undefined.
 * @param {*} param - The parameter to check.
 * @returns {boolean} - Returns true if the parameter is set, false otherwise.
 */
export function isSet(param) {
  return param !== null && param !== undefined
}

const channels = {
  'bugsnag_customer-dashboard':
    'https://hooks.slack.com/services/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',
  engineering:
    'https://hooks.slack.com/services/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',
  vertrieb:
    'https://hooks.slack.com/services/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',
  'kunden-controlling':
    'https://hooks.slack.com/services/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'
}

export async function sendSlackMessage(channel, message, throwError) {
  // Ein String bleibt ein String – die bestehenden Call-Sites aendern sich
  // nicht. Ein Objekt wird als Block-Kit-Payload durchgereicht. `text` bleibt
  // darin Pflicht: Slack zeigt in Push-Notification, Sidebar und
  // Notification-Preview ausschliesslich `text`, niemals die Blocks.
  const payload = typeof message === 'string' ? { text: message } : message

  // because of cors policy
  if (NODE_ENV === 'development') {
    return console.log('send-slack-message:', payload) // eslint-disable-line
  }

  try {
    // Die Response geht an den Aufrufer. fetch löst auch bei 4xx und 5xx auf,
    // etwa bei widerrufenem Webhook, wer das wissen muss, prüft response.ok
    // (features/pricing/pricing-slack.js).
    return await window.fetch(channels[channel], {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        // Haelt den Request im CORS-Simple-Request-Fenster (kein Preflight) –
        // Slack-Webhooks antworten nicht auf OPTIONS. Der Body ist trotzdem
        // JSON, das akzeptiert Slack. Nicht auf application/json aendern.
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: JSON.stringify(payload)
    })
  } catch (err) {
    notifyBugsnag(err)

    if (throwError) {
      throw err
    }
  }
}

export const convertGraphqlError = (error, others) => {
  const { errors } = error
  const { query, mutation, variables, input } = others || {}

  if (
    // graphql error
    Array.isArray(errors)
  ) {
    const { message, path } = errors[0]
    const error_path = path && path[0] !== query && path[0]
    const additionalInfo =
      (variables && `variables: ${JSON.stringify(variables)}`) ||
      (input && `input: ${JSON.stringify(input)}`)

    return new Error(
      [
        query || mutation,
        message,
        error_path,
        additionalInfo,
        GRAPHQL_ERROR_FLAG
      ]
        .filter((item) => item)
        .join(' / ')
    )
  }

  return new Error(`convertGraphqlError:  ${JSON.stringify(error)} (unknown)`)
}

const isGraphqlError = ({ message }) => {
  return message.toLowerCase().includes(GRAPHQL_ERROR_FLAG)
}

export const createErrorLog = async ({ event: bugsnag_event }) => {
  const { partner_id, user_id } = bugsnag_event._metadata.meta

  const message = bugsnag_event.errors[0].errorMessage
  const event_name = isGraphqlError({ message })
    ? 'GRAPHQL_ERROR'
    : 'JAVASCRIPT_ERROR'

  try {
    await mutation({
      mutation: 'createLog',
      input: {
        type: 'ERROR',
        event_name,
        data: JSON.stringify(bugsnag_event),
        source: partner_id,
        fired_by_id: user_id,
        authorized_create: ['__create_' + partner_id],
        authorized_read: ['__read_low_' + partner_id]
      },
      options: { disableLastUpdatedBy: true }
    })
  } catch (err) {}
}

export const isNetworkError = ({ message }) => {
  return (
    message.toLowerCase().includes('failed to fetch') ||
    message.toLowerCase().includes('network error') ||
    message.toLowerCase().includes('networkerror') ||
    message.toLowerCase().includes('no current user') ||
    message.toLowerCase().includes('load failed') ||
    message.toLowerCase().includes('timeout exceeded')
  )
}

/**
 * if we use src/graphql/index.js query / mutation
 * everywhere, we can remove the check in a while
 *
 * src/index.js
 * the console.error function has been overwritten
 * with Bugsnag.notify (maybe we could use instead
 * of notifyBugsnag console.error for catches)
 *
 * */
export const notifyBugsnag = (error) => {
  console.error(isError(error) ? error : convertGraphqlError(error))
}

export const resizeFile = (file, maxWidth = 375, maxHeight = 250) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      'png',
      100,
      0,
      (uri) => {
        resolve(uri)
      },
      'file'
    )
  })

export const convertNumberToTime = (number) => {
  // separate the int from the decimal part
  const minutes = Math.floor(number)
  let decpart = number - minutes

  const min = 1 / 60
  // round to nearest minute
  decpart = min * Math.round(decpart / min)

  const seconds = Math.floor(decpart * 60) + ''

  // concate minutess and seconds
  return { minutes, seconds }
}

export const toBase64 = (obj) => {
  return Buffer.from(JSON.stringify(obj)).toString('base64')
}

export const fromBase64 = (base64String) => {
  try {
    // URLSearchParams macht aus '+' ein Leerzeichen, aeltere Links enthalten es so
    const normalized = String(base64String).replace(/ /g, '+')
    return JSON.parse(Buffer.from(normalized, 'base64'))
  } catch (err) {
    notifyBugsnag(new Error(`parse link error (from base 64) - ${err.message}`))
  }
}

export const checkForUserBranch = () => {
  if (process.env.NODE_ENV === 'development') return

  const branch = process.env.REACT_APP_USER_BRANCH

  if (!branch) {
    console.warn('REACT_APP_USER_BRANCH missing')
    return
  }

  if (!Object.values(STAGES).includes(branch)) {
    console.warn(`Unknown branch '${branch}' - resolved stage: '${STAGE}'`)
  }
}

export const initializeFirebase = () => {
  if (BUILD_ENV !== 'production') return
  initializeApp(firebaseConfig)
}

/**
 * Helper function for the system useQuery used in App.js
 * @param {Object} system
 */
export function selectSystemData(rawItems) {
  // query() resolvt ein nicht existierendes System-Item mit null, nicht mit
  // einem Fehler (graphql/index.js:41). Ohne diesen Filter destrukturiert das
  // ANNOUNCEMENT-find weiter unten { id } aus null, sobald ein Item VOR
  // ANNOUNCEMENT in use-system-query.js fehlt - der TypeError wird dort im
  // select weggefangen, system bleibt undefined und App.js rendert dauerhaft
  // den Dashboard-Loader.
  const items = rawItems.filter(Boolean)

  let result = {
    mail_templates: items.find(({ id }) => id === 'MAIL_TEMPLATES'),
    challenges: items.find(({ id }) => id === 'CHALLENGES'),
    categories: items.find(({ id }) => id === 'CATEGORIES'),
    dimensions: items.find(({ id }) => id === 'DIMENSIONS'),
    dimensions_pdf_reports: items.find(
      ({ id }) => id === 'DIMENSIONS_PDF_REPORTS'
    )
  }

  Object.keys(result).forEach((key) => {
    if (!result[key]) throw new Error('system: !result[key]')

    result[key] = { ...result[key], ...JSON.parse(result[key].data) }
  })

  const {
    DEFAULT_CAREER_MAIL_TEMPLATE,
    DEFAULT_CAREER_MAIL_TEMPLATE_REMINDER,
    DEFAULT_PARTNER_MAIL_TEMPLATE,
    DEFAULT_PARTNER_MAIL_TEMPLATE_REMINDER,
    DEFAULT_CROSSMODE_MAIL_TEMPLATE,
    DEFAULT_CAREER_MAIL_TEMPLATE_REJECT,
    DEFAULT_PARTNER_MAIL_TEMPLATE_REJECT,
    SCAFFOLD,
    INVITATION_BLOCK
  } = result.mail_templates

  result = {
    ...result,
    defaultCareerMailTemplate: DEFAULT_CAREER_MAIL_TEMPLATE,
    defaultCareerMailTemplateReminder: DEFAULT_CAREER_MAIL_TEMPLATE_REMINDER,
    defaultPartnerMailTemplate: DEFAULT_PARTNER_MAIL_TEMPLATE,
    defaultPartnerMailTemplateReminder: DEFAULT_PARTNER_MAIL_TEMPLATE_REMINDER,
    defaultCrossmodeMailTemplate: DEFAULT_CROSSMODE_MAIL_TEMPLATE,
    defaultCareerMailTemplateReject: DEFAULT_CAREER_MAIL_TEMPLATE_REJECT,
    defaultPartnerMailTemplateReject: DEFAULT_PARTNER_MAIL_TEMPLATE_REJECT,
    scaffold: SCAFFOLD,
    invitationBlock: INVITATION_BLOCK
  }

  const announcement = JSON.parse(
    items.find(({ id }) => id === 'ANNOUNCEMENT').data
  ).find(({ application }) => application === 'AIVY_CUSTOMER_DASHBOARD')

  const currentChallengeConfigId = items.find(
    (item) => item.id === 'CURRENT_CHALLENGE_CONFIG_ID'
  )

  const dimensionsCultureItem = items.find(
    ({ id }) => id === 'DIMENSIONS_CULTURE'
  )
  const dimensions_culture = dimensionsCultureItem
    ? JSON.parse(dimensionsCultureItem.data)
    : null

  return {
    ...result,
    dimensions_culture,
    announcement,
    currentChallengeConfigId: JSON.parse(currentChallengeConfigId.data).id
  }
}

const WEBAPP_URL = {
  [STAGES.DEVELOP]:
    'http://develop.webapp.aivy.app.s3-website.eu-central-1.amazonaws.com',
  [STAGES.STAGING]:
    'http://staging.webapp.aivy.app.s3-website.eu-central-1.amazonaws.com',
  [STAGES.PRODUCTION]: 'https://webapp.aivy.app'
}

// Die Webapp muss gegen dasselbe Backend laufen wie das Dashboard, das den
// Space angelegt hat – sonst findet sie die space_id nicht. Deshalb STAGE
// (Backend) und nicht BUILD_ENV (Branchname), siehe ./stage.
export const getInviteToAssessmentLink = ({ space_id, language = DE }) => {
  const url = new URL(WEBAPP_URL[STAGE])

  url.searchParams.set('invitation', space_id)
  url.searchParams.set('language', language)

  return url.toString()
}

// Start-Link der Webapp ohne Einladung, etwa als Platzhalter in der
// Mail-Vorschau. Den persönlichen Link gibt es erst mit dem Space.
export const getAssessmentStartLink = ({ language = DE } = {}) => {
  const url = new URL(WEBAPP_URL[STAGE])

  url.searchParams.set('language', language)

  return url.toString()
}

// Die Webapp kann mehr Sprachen als das Dashboard (nl, fr). Die
// Dashboard-Sprache wird deshalb nur uebernommen, wenn die Challenge sie
// anbietet, sonst de.
const resolveTryoutLanguage = ({ language, available_languages }) => {
  if (!Array.isArray(available_languages) || available_languages.length === 0) {
    return language
  }

  if (available_languages.includes(language)) return language

  return available_languages.includes(DE) ? DE : available_languages[0]
}

// Ohne ?language= raet die Webapp selbst (localStorage, dann Browsersprache,
// dann de) und im Challenge-Demo gibt es keinen Sprachumschalter. Ein
// englisches Dashboard landete so in einer deutschen Challenge.
export const getTryoutChallengeLink = ({
  exam_id,
  language = DE,
  available_languages
}) => {
  const url = new URL(WEBAPP_URL[STAGE])

  url.searchParams.set('challenge', exam_id)
  url.searchParams.set(
    'language',
    resolveTryoutLanguage({ language, available_languages })
  )

  return url.toString()
}

export const colorByString = (s, customColorsArray) => {
  if (typeof s !== 'string')
    return { backgroundColor: 'bg-gray-100', text: 'text-gray-600' } // DEFAULT

  let sumChars = 0
  for (let i = 0; i < s.length; i += 1) {
    sumChars += s.charCodeAt(i)
  }

  // inspired by https://github.com/wbinnssmith/react-user-avatar
  // colors from https://flatuicolors.com/
  const colors = [
    { backgroundColor: 'bg-gray-100', text: 'text-gray-600' },
    { backgroundColor: 'bg-red-100', text: 'text-red-700' },
    { backgroundColor: 'bg-yellow-100', text: 'text-yellow-800' },
    { backgroundColor: 'bg-green-100', text: 'text-green-700' },
    { backgroundColor: 'bg-blue-100', text: 'text-blue-700' },
    { backgroundColor: 'bg-indigo-100', text: 'text-indigo-700' },
    { backgroundColor: 'bg-purple-100', text: 'text-purple-700' },
    { backgroundColor: 'bg-pink-100', text: 'text-pink-700' },
    { backgroundColor: 'bg-slate-100', text: 'text-slate-700' },
    { backgroundColor: 'bg-zinc-100', text: 'text-zinc-700' },
    { backgroundColor: 'bg-stone-100', text: 'text-stone-700' },
    { backgroundColor: 'bg-orange-100', text: 'text-orange-700' },
    { backgroundColor: 'bg-orange-100', text: 'text-orange-700' },
    { backgroundColor: 'bg-emerald-100', text: 'text-emerald-700' },
    { backgroundColor: 'bg-fuchsia-100', text: 'text-fuchsia-700' }
  ]

  if (customColorsArray) {
    return customColorsArray[sumChars % colors.length]
  }

  return colors[sumChars % colors.length]
}

export const replaceMacros = ({ text: t, macros }) => {
  let text = t

  // Platzhalter ohne Wert bleiben stehen. Die Funktion verhindert, dass $& oder
  // $1 in einem Wert (z. B. Firmenname) als Ersetzungsmuster gelesen werden.
  macros.forEach(({ macro, value }) => {
    if (value === undefined || value === null) return
    text = text.replaceAll(macro, () => value)
  })

  return text
}

export const startStrWithCapital = (str) => {
  if (str === null || str === undefined) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const isEmptyObj = (obj) => {
  if (obj === null || obj === undefined) return true
  if (!(typeof obj === 'object' && obj !== null)) return true

  return Object.keys(obj).length === 0
}

export const getRequirementAnalyseLink = ({ analyse_id }) => {
  const DEVELOP_URL = new URL('https://develop.anforderungsanalyse.aivy.app')
  const STAGING_URL = new URL('https://staging.anforderungsanalyse.aivy.app')
  const PRODUCTION_URL = new URL('https://www.anforderungsanalyse.aivy.app')

  DEVELOP_URL.searchParams.set('invitation', analyse_id)
  STAGING_URL.searchParams.set('invitation', analyse_id)
  PRODUCTION_URL.searchParams.set('invitation', analyse_id)

  switch (BUILD_ENV) {
    case 'develop':
      return DEVELOP_URL.toString()
    case 'staging':
      return STAGING_URL.toString()
    default:
      return PRODUCTION_URL.toString()
  }
}

export const isHubspotChatLoaded = () => {
  return !!document.getElementById(HUBSPOT_IFRAME_ID)
}

/**
 * Extracts domain from email address
 * @param {string} email - Email address
 * @returns {string} Domain part of the email
 */
export const extractDomainFromEmail = (email) => {
  if (!email || typeof email !== 'string') return null
  const parts = email.split('@')
  return parts.length === 2 ? parts[1] : null
}

/**
 * Fetches company logo and name from Brandfetch CDN and Search API
 * @param {string} domain - Company domain
 * @returns {Promise<Object|null>} Brand data with logo URL and company name
 */
export const fetchCompanyBrand = async (domain) => {
  if (!domain) return null

  try {
    // Construct logo URL directly from CDN
    const logoUrl = `https://cdn.brandfetch.io/${domain}/w/512/h/512/logo?c=1idaBzOYytx2NbVwAFA`

    // Fetch company name from search API
    const searchResponse = await fetch(
      `https://api.brandfetch.io/v2/search/${domain}?c=1idaBzOYytx2NbVwAFA`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )

    let companyName = null
    if (searchResponse.ok) {
      const searchData = await searchResponse.json()

      // Extract company name from search results
      if (searchData && searchData.length > 0) {
        companyName = searchData[0]?.name
      }

      return {
        name: companyName,
        domain,
        logoUrl,
        searchData
      }
    }
    return null
    // Return logo URL directly - let the img tag handle loading errors
  } catch (error) {
    console.error('Error fetching brand data:', error)
    return null
  }
}

const getCookieValue = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

/**
 * Checks if the user has given consent for a specific category
 * @param {"targeting", "unclassified", "performance"} category
 * @returns
 */
export const hasConsentFor = (category) => {
  const cookie = getCookieValue('CookieScriptConsent')
  if (!cookie) return false

  try {
    const parsed = JSON.parse(cookie)
    return parsed.categories.includes(category)
  } catch (e) {
    console.warn('Consent Cookie konnte nicht geparst werden', e)
    return false
  }
}

export const truncateCareerID = (id, maxLength = 12) => {
  if (!id) return ''
  if (id.length <= maxLength) return id
  return `${id.slice(0, Math.floor(maxLength / 2))}...${id.slice(-Math.floor(maxLength / 2))}`
}

export const trackAdsConversion = (eventName) => {
  if (BUILD_ENV !== 'production') return
  if (typeof window === 'undefined') return
  if (!eventName) {
    throw new Error(
      'Event name is required for Microsoft Ads conversion tracking'
    )
  }

  // Microsoft UET
  window.uetq = window.uetq || []
  window.uetq.push('event', eventName, {})

  console.warn('UET event tracked:', eventName)

  // Google Ads
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName
  })

  console.warn('Google Ads event tracked:', eventName)
}

export function delayedAction(fn, delay = 500) {
  setTimeout(fn, delay)
}
